import {Attributes, AttributesRow, DataStatus} from 'platform/components';
import {Text} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetWarrantyProgramQuery, useGetWatermarkListQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {getAvailableLocales, useTenant} from '@dms/shared';

import {buildArray, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getTermsAndConditionsLabel} from '../utils/getTermsAndConditionsLabel';

interface WarrantyProgramDetailProps extends RequiredTestIdProps {
  warrantyProgramId: string;
}

export function WarrantyProgramDetail(props: WarrantyProgramDetailProps) {
  const {
    data: program,
    isLoading,
    isError,
  } = useGetWarrantyProgramQuery({
    warrantyProgramId: props.warrantyProgramId,
  });
  const {data: watermarks} = useGetWatermarkListQuery();
  const {tenant} = useTenant();

  const watermarkName = (watermarks ?? []).find(
    (watermark) => watermark.id === program?.watermarkId
  )?.name;

  const termsAndConditionsRows = getAvailableLocales(tenant).map<AttributesRow>((locale) => {
    const label = getTermsAndConditionsLabel(locale);
    const text = program?.termsAndConditions.find((item) => item.locale === locale)?.text;

    return isNilOrEmpty(text)
      ? {label, value: null}
      : {
          label,
          content: (
            <Text size="xSmall" noWrap title={text}>
              {text}
            </Text>
          ),
        };
  });

  const rows = buildArray<AttributesRow>()
    .add({label: i18n.t('general.labels.name'), value: program?.name})
    .add({
      label: i18n.t('entity.warranty.labels.defaultDuration'),
      value: program?.duration ?? null,
    })
    .add({
      label: i18n.t('entity.warranty.labels.watermark'),
      value: watermarkName ?? null,
    })
    .add(...termsAndConditionsRows);

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={20}>
      <Attributes
        size="third"
        rows={rows}
        isValueColumnShrinkable
        data-testid={suffixTestId('attributes', props)}
      />
    </DataStatus>
  );
}
